import React, { useState, useMemo, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Upload, Trash2, Copy, Image as ImageIcon, Search, Loader2 } from 'lucide-react';
import AdminNavTabs from './components/AdminNavTabs';
import { uploadsAbsoluteUrl } from '../../utils/uploadsBase';
import './NewAdminPanel.css';
import './AdminMediaLibrary.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Только списки медиа. НЕ инвалидируем adminPages — иначе после N загрузок
 * получается N тяжёлых GET /admin/pages подряд → обрыв соединения / Network Error.
 */
function invalidateMediaListQueries(queryClient) {
  queryClient.invalidateQueries({
    predicate: (q) => {
      const k = q.queryKey;
      if (!Array.isArray(k) || k.length === 0) return false;
      const first = String(k[0]);
      return first.includes('adminMedia') || first === 'adminMediaLibrary';
    },
  });
}

const AdminMediaLibrary = () => {
  const [token] = useState(() => localStorage.getItem('admin_token'));
  const [search, setSearch] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);
  const mediaInvalidateTimerRef = useRef(null);
  const queryClient = useQueryClient();

  const scheduleMediaListInvalidate = useCallback(() => {
    if (mediaInvalidateTimerRef.current) {
      clearTimeout(mediaInvalidateTimerRef.current);
    }
    mediaInvalidateTimerRef.current = setTimeout(() => {
      mediaInvalidateTimerRef.current = null;
      invalidateMediaListQueries(queryClient);
    }, 400);
  }, [queryClient]);

  React.useEffect(() => {
    if (!token) window.location.href = '/admin/login';
  }, [token]);

  React.useEffect(
    () => () => {
      if (mediaInvalidateTimerRef.current) {
        clearTimeout(mediaInvalidateTimerRef.current);
      }
    },
    [],
  );

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['adminMediaLibrary', token],
    queryFn: async () => {
      const r = await axios.get(`${API_URL}/admin/media`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return r.data.media || [];
    },
    enabled: !!token,
  });

  const mediaList = data || [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return mediaList;
    return mediaList.filter(
      (m) =>
        String(m.filename || '')
          .toLowerCase()
          .includes(q) ||
        String(m.url || '')
          .toLowerCase()
          .includes(q) ||
        String(m.id).includes(q),
    );
  }, [mediaList, search]);

  const uploadMutation = useMutation({
    mutationFn: async (file) => {
      const form = new FormData();
      form.append('file', file);
      const r = await axios.post(`${API_URL}/admin/upload`, form, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      return r.data;
    },
    onSuccess: () => {
      scheduleMediaListInvalidate();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      await axios.delete(`${API_URL}/admin/media/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    },
    onSuccess: () => {
      scheduleMediaListInvalidate();
    },
  });

  /** По очереди — меньше одновременной нагрузки на БД/диск при выборе десятков файлов */
  const handleFiles = async (files) => {
    if (!files?.length) return;
    for (const f of Array.from(files)) {
      try {
        await uploadMutation.mutateAsync(f);
      } catch (e) {
        console.error('Upload failed:', f?.name, e);
      }
    }
  };

  const copyUrl = (url) => {
    const full = uploadsAbsoluteUrl(url, API_URL);
    navigator.clipboard.writeText(full).then(
      () => alert('Ссылка скопирована в буфер обмена'),
      () => prompt('Скопируйте ссылку:', full),
    );
  };

  const onDelete = (item) => {
    if (
      !window.confirm(
        `Удалить файл «${item.filename}» (#${item.id})?\nЕсли он используется на странице, удаление может завершиться ошибкой.`,
      )
    ) {
      return;
    }
    deleteMutation.mutate(item.id, {
      onError: (err) => {
        const msg =
          err?.response?.data?.error || err?.message || 'Ошибка удаления';
        alert(msg);
      },
    });
  };

  if (!token) return null;

  if (error) {
    const status = error?.response?.status;
    if (status === 401 || status === 403) {
      localStorage.removeItem('admin_token');
      window.location.href = '/admin/login';
      return null;
    }
    return (
      <div className="new-admin-panel">
        <header className="admin-header">
          <div className="header-left">
            <h1>Медиатека</h1>
            <AdminNavTabs />
          </div>
        </header>
        <div className="media-library-error">
          <p>{error?.response?.data?.error || error.message}</p>
          <button type="button" onClick={() => refetch()}>
            Повторить
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="new-admin-panel">
      <header className="admin-header">
        <div className="header-left">
          <h1>Медиатека</h1>
          <AdminNavTabs />
        </div>
        <div className="header-right">
          <button
            type="button"
            className="logout-btn"
            onClick={() => {
              localStorage.removeItem('admin_token');
              window.location.href = '/admin/login';
            }}
          >
            Выйти
          </button>
        </div>
      </header>

      <main className="media-library-main">
        <div className="media-library-toolbar">
          <div
            className={`media-upload-zone ${dragActive ? 'drag-active' : ''}`}
            onDragEnter={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setDragActive(false);
            }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              setDragActive(false);
              handleFiles(e.dataTransfer.files);
            }}
            onClick={() => inputRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
            }}
          >
            <input
              ref={inputRef}
              type="file"
              accept="image/*,video/*"
              multiple
              className="media-upload-input"
              onChange={(e) => {
                handleFiles(e.target.files);
                e.target.value = '';
              }}
            />
            {uploadMutation.isPending ? (
              <Loader2 className="spin" size={32} />
            ) : (
              <Upload size={32} />
            )}
            <p>
              <strong>Загрузить файлы</strong> — перетащите сюда или нажмите
            </p>
            <p className="media-upload-hint">Изображения и видео сохраняются на сервер и появляются в списке ниже.</p>
          </div>

          <div className="media-library-search">
            <Search size={18} />
            <input
              type="search"
              placeholder="Поиск по имени, URL или ID…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {isLoading ? (
          <div className="media-library-loading">
            <div className="spinner" />
            <span>Загрузка списка…</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="media-library-empty">
            <ImageIcon size={48} />
            <p>Файлов пока нет. Загрузите изображения выше.</p>
          </div>
        ) : (
          <div className="media-library-grid">
            {filtered.map((item) => {
              const src = uploadsAbsoluteUrl(item.url, API_URL);
              const isVideo = /\.(mp4|webm|mov)$/i.test(item.url || '');
              return (
                <article key={item.id} className="media-card">
                  <div className="media-card-preview">
                    {isVideo ? (
                      <video src={src} muted playsInline />
                    ) : (
                      <img src={src} alt={item.filename || ''} loading="lazy" />
                    )}
                  </div>
                  <div className="media-card-body">
                    <div className="media-card-title" title={item.filename}>
                      {item.filename || item.url}
                    </div>
                    <div className="media-card-meta">#{item.id}</div>
                    <div className="media-card-actions">
                      <button
                        type="button"
                        className="media-btn media-btn-secondary"
                        onClick={() => copyUrl(item.url)}
                      >
                        <Copy size={16} /> URL
                      </button>
                      <button
                        type="button"
                        className="media-btn media-btn-danger"
                        disabled={deleteMutation.isPending}
                        onClick={() => onDelete(item)}
                      >
                        <Trash2 size={16} /> Удалить
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminMediaLibrary;
